'use strict';
/* ==========================================================================
   levels/sides.js — step "sides" (orientation any): WHICH SIDE? (DESIGN.md §5.2).

   An attempt = 8 cards from content/bank.sides.json, drawn by the model (Ratify.attempt('sides', json, {fresh}): easiest
   first, ≥ 3 per side, ≥ 1 EOC card, every issue once, the last attempt's cards avoided, the seed saved in
   Game.progress.bank.sides). Nothing here decides a side: each card carries `side`, `sq` (step 1, a bank instance) and
   `rq` (step 2, "Why would they say it?", 3 reasons shuffled); Bank.check() marks the picks.

   Two kinds of card:
     statement  the card shows a viewpoint (a verified quote shows its speaker as small story text, never the key);
                sq = "Which side would say this?" Federalist / Anti-Federalist: ONE pick; the truth is stamped on the
                card either way, and a wrong pick gets sq.why (the side's reason)
     eoc        the FLDOE sample format: the card asks "Which statement supports the <side>?"; sq = 4 statements; a
                wrong pick is crossed out with its why, pick again; once solved the card shows the key statement
   Then rq: a wrong reason is crossed out with its why, pick again → the verdict: the card slides onto its side of the
   printing-press tray (Federalist = navy, left; Anti-Federalist = brick, right; Civic.SIDE), the Judge's `explain`
   line (text only, no new voice: the free-speech caption pattern) → next card.
   Stars: step 1 first try 1 + step 2 first try 1; of = 16, pass = bank.sides.json passStars (11).
   Game.progress.scores.sides = {best, last, of}; first answers under Game.progress.answers['sides/<key>/side'] and
   ['sides/<key>']. A pass → "Back to the map" stamps the step (Game.complete). Resume: Game.progress.bank.sides.run =
   {i, ev, res}: a reload mid-attempt replays the same card's taps.

   Portrait phone (Dock.active): the card and the tray are the picture on the stage, the Judge beside them; the
   statement, the options and the Judge's line are dock widgets.
   Keys: statement: 1 / F = Federalist, 2 / A = Anti-Federalist; eoc: 1–4; reasons: 1–3; Enter / Space = start / next.

   QA hooks: sc.phase ('intro' | 'drop' | 'sq' | 'rq' | 'verdict' | 'end'), sc.i, sc.card, sc.cards, sc.seed,
   sc.attempt, sc.resumed, sc.results, sc.stars, sc.sPicks, sc.picks, sc.say, sc.answer() → {kind, side, sIndex,
   rIndex}, sc.pickSide(i), sc.chooseSide('federalist' | 'anti'), sc.choose(i), sc.next(), sc.begin(fresh),
   sc.start(), sc.end(), sc.endButtons(), sc.finish() (tests: end now + stamp), sc.lay(), sc.tray(),
   Levels.sides.solution, Levels.sides.preview(id) (swap the current card for a pool item: long-text screenshots).
   ========================================================================== */
(function () {
  const STEP = 'sides', TITLE = 'Which Side?';
  const DROP = 0.6, NEXT_GUARD = 0.3, SLIDE = 0.6;
  const CV = () => window.Civic;
  const C = () => CV().C;
  const json = () => Game.content.bank_sides || { items: [], draw: {} };
  const SLAB = "'Alfa Slab One'", SERIF = "'Playfair Display'", TYPE = "'Special Elite'", HAND = "'Caveat Brush'";
  const INK = '#2b2420', PENCIL = '#5b6475', WOOD = '#5a4632', RULE_BLUE = 'rgba(70,110,170,0.28)';
  const sfx = (name, alt, o) => { const n = Audio.has(name) ? name : alt; if (n && Audio.has(n)) Audio.play(n, o); };
  const measure = (s, font) => { g.save(); g.font = font; const w = g.measureText(s).width; g.restore(); return w; };
  const round = (v, k) => Math.round(v / k) * k;
  const lerp = (a, b, u) => a + (b - a) * u;
  const ucFirst = s => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const SIDE = side => CV().SIDE[side] || CV().SIDE.federalist;

  /* a side's badge: a paper disc in the side's colour with its woodtype letter (F / A) */
  function sideBadge(side, x, y, size, { s = 1, seed = 61, rot = 0 } = {}) {
    if (s <= 0.001) return;
    const S = SIDE(side), r = Math.round(size * 0.5);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperCircle(0, 0, r, S.color, { seed, depth: 0.9 });
    g.font = `${Math.round(size * 0.52)}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = S.ink;
    g.fillText(S.letter, 0, size * 0.04);
    g.restore();
  }

  /* the card: geometry + the text fitted (cached per card, text, UI scale, dock) */
  function cardBox(docked) { return docked ? { x: 760, top: 136, w: 1400, h: 506 } : { x: 640, top: 190, w: 1160, h: 440 }; }
  function layoutCard(txt, { docked, question = false, speaker = null } = {}) {
    const K = cardBox(docked);
    const left = K.x - K.w / 2, right = K.x + K.w / 2, bottom = K.top + K.h;
    const bs = docked ? 44 : Math.round(UI.text(28));                          // the header badge's type
    const ruleY = K.top + Math.max(92, bs * 2.4);
    const fs = docked ? 40 : Math.round(UI.text(26));                          // the footer line (No. / speaker)
    const tx = left + 70, tw = K.w - 140, tTop = ruleY + 26, tBot = bottom - Math.max(58, fs * 2.1);
    const font = question ? SLAB : TYPE;
    const sMax = docked ? 84 : 62, sMin = docked ? 44 : Math.round(Math.max(30, UI.text(30)));
    let s = sMax, lines;
    for (; s >= sMin; s -= 2) {
      lines = UI._wrap(txt, tw, `${s}px ${font}`);
      if (lines.length * s * 1.3 <= tBot - tTop && lines.every(l => measure(l, `${s}px ${font}`) <= tw)) break;
    }
    if (s < sMin) { s = sMin; lines = UI._wrap(txt, tw, `${s}px ${font}`); }
    const lh = s * 1.3, blockH = lines.length * lh;
    const y0 = tTop + Math.max(0, (tBot - tTop - blockH) * 0.4);
    const spk = speaker ? '— ' + speaker : '';
    return { ...K, left, right, bottom, cy: K.top + K.h / 2, bs, ruleY, fs, tx, tw, tTop, tBot, s, lh, font, lines, y0,
      lineY: lines.map((_, j) => y0 + (j + 0.5) * lh), spk,
      over: lines.map(l => measure(l, `${s}px ${font}`) - tw).filter(v => v > 0.5)
        .concat(spk ? [measure(spk, `${fs}px ${HAND}`) + 260 - K.w + 88].filter(v => v > 0.5) : []),
      fits: y0 + blockH <= tBot + 0.5 };
  }
  /* the printing-press tray: two compartments, Federalist (navy) left, Anti-Federalist (brick) right */
  function trayGeo(docked) {
    const T0 = docked ? { x: 760, y: 868, w: 1400, h: 376 } : { x: 640, y: 858, w: 1180, h: 356 };
    const cw = round(T0.w / 2 - 44, 10), ch = round(T0.h - 60, 10), K = cardBox(docked);
    const comp = side => ({ x: T0.x + (side === 'federalist' ? -1 : 1) * (T0.w / 4 - 6), y: T0.y + 8, w: cw, h: ch });
    const sIn = (cw - 70) / K.w;
    return { ...T0, cw, ch, comp, sIn, top: T0.y - T0.h / 2 };
  }

  class Sides extends Scene {
    get n() { return this.cards ? this.cards.length : 8; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get card() { return this.cards ? this.cards[this.i] : null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() { return this.answer(); }
    answer() { const cd = this.card; return cd ? { kind: cd.kind, side: cd.side, sIndex: cd.sq.answerIndex, rIndex: cd.rq.answerIndex } : null; }
    get sSolved() { const cd = this.card; return !!cd && (cd.kind === 'statement' ? this.sPicks.length > 0 : this.sPicks.includes(cd.sq.answerIndex)); }

    enter() {
      this.phase = 'intro'; this.cards = null; this.i = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map(); this.sPicks = []; this.picks = [];
      Audio.play('paper', { gain: 0.5 });
      this.begin(false);
    }
    exit() { this.bankStamp(); }

    /* ---- the attempt (Ratify.attempt: seeded, saved in Game.progress.bank.sides; + our run for resuming) ---- */
    begin(fresh = false) {
      this.bankStamp();
      const P_ = Game.progress, S0 = P_.bank && P_.bank[STEP];
      const run = !fresh && S0 && S0.open && S0.run ? S0.run : null;
      const a = Ratify.attempt(STEP, json(), { fresh });
      this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed; this.cards = a.cards;
      this.results = []; this.passed = false; this.collected = false; this.done = false; this.lays.clear();
      this.setup(0);
      if (a.resumed && run && this.restore(run)) return;
      this.phase = 'intro';
      if (fresh) this.start();                                                   // "Try again": straight to card 1
    }
    start() { if (this.cards && this.phase === 'intro') this.startCard(0); }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.cards.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.cardAt = Game.time - DROP - 1; this.phase = 'sq';
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 's') this.pickSide(v); else if (k === 'c') this.choose(v); }
        this.quiet = false;
        if (this.qAt) this.qAt = Math.min(this.qAt, Game.time);
        if (this.verdictAt) this.verdictAt = Math.min(this.verdictAt, Game.time - SLIDE);
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[sides] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.i = i; this.ev = []; this.sPicks = []; this.picks = []; this.say = null;
      this.cardAt = Game.time; this.sAt = this.sPickedAt = this.qAt = this.pickedAt = this.verdictAt = null;
      this._oc = this._vc = null;
    }
    startCard(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.play('paper', 'whoosh', { gain: 0.55 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end' && this.phase !== 'intro') S.run = { i: this.i, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    play(name, alt, o) { if (!this.quiet) sfx(name, alt, o); }

    /* ---- step 1: which side? (statement: one pick, the truth stamped; eoc: pick until right) ---- */
    pickSide(i) {
      const cd = this.card, q = cd && cd.sq;
      if (this.phase === 'drop') this.phase = 'sq';
      if (this.phase !== 'sq' || !q || !(i >= 0 && i < q.options.length) || this.sPicks.includes(i)) return false;
      this.ev.push(['s', i]);
      if (!this.sPicks.length) Game.progress.answer(STEP + '/' + cd.key + '/side', q.options[i]);
      this.sPicks.push(i); this.sPickedAt = Game.time;
      const right = Bank.check(q, i);
      if (cd.kind === 'statement') {
        this.say = right ? null : { text: 'Not quite: ' + (q.why[i] || `${an(Ratify.SIDE_LABEL[cd.side])} ${Ratify.SIDE_LABEL[cd.side]} idea.`), at: Game.time };
        this.toReasons();
        this.play('stamp', null, { gain: 0.6 }); if (!right) this.play('thunk', null, { gain: 0.35, rate: 0.9 });
      } else if (right) { this.toReasons(); this.play('stamp', null, { gain: 0.6 }); this.play('ding', null, { gain: 0.5 }); }
      else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this._oc = null;
      this.save();
      return true;
    }
    chooseSide(side) { const cd = this.card; if (!cd || cd.kind !== 'statement') return false; return this.pickSide(cd.sq.options.indexOf(Ratify.SIDE_LABEL[side])); }
    toReasons() { this.sAt = Game.time; this.qAt = Game.time + 0.7; this.phase = 'rq'; }
    /* ---- step 2: why would they say it? ---- */
    choose(i) {
      const cd = this.card, q = cd && cd.rq;
      if (this.phase !== 'rq' || !q || !(i >= 0 && i < q.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + cd.key, q.options[i]);
      this.picks.push(i); this.pickedAt = Game.time; this._oc = null;
      if (Bank.check(q, i)) { this.play('ding', null, { gain: 0.6 }); this.toVerdict(); }
      else this.play('thunk', null, { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toVerdict() {
      const cd = this.card, ss = this.sPicks[0] === cd.sq.answerIndex ? 1 : 0, rs = this.picks.length === 1 ? 1 : 0;
      this.phase = 'verdict'; this.verdictAt = Game.time;
      this.results[this.i] = { i: this.i, key: cd.key, kind: cd.kind, side: cd.side, sStar: ss, rStar: rs, stars: ss + rs,
        wrongPicks: this.sPicks.length - 1 + this.picks.length - 1 };
      this.play('whoosh', 'paper', { gain: 0.4 });
    }
    next() {
      if (this.phase !== 'verdict' || Game.time - this.verdictAt < NEXT_GUARD) return false;
      if (this.i < this.cards.length - 1) this.startCard(this.i + 1);
      else this.end();
      return true;
    }
    end() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S) delete S.run;
      Ratify.finish(STEP);                                                       // the next attempt draws new cards
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    /* tests only: end the attempt now with the stars so far and stamp the step */
    finish() {
      if (this.done) return; this.done = true;
      if (!this.cards) this.begin(false);
      if (this.phase !== 'end') this.end();
      this.collected = true; Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Back to the map ▶', go: () => this.collect() }, { label: 'Try again (new cards)', go: () => this.begin(true) }]
        : [{ label: 'Try again (new cards)', go: () => this.begin(true) }, { label: 'Back to the map', go: () => Game.replace(new Levels.map()) }];
    }

    update() { if (this.phase === 'drop' && Game.time - this.cardAt > DROP + 0.1) this.phase = 'sq'; }
    keyDown(k) {
      const n = parseInt(k, 10), cd = this.card;
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.start(); }
      else if (this.phase === 'drop' || this.phase === 'sq') {
        if (cd.kind === 'statement') { if (n === 1 || k === 'f' || k === 'F') this.pickSide(0); else if (n === 2 || k === 'a' || k === 'A') this.pickSide(1); }
        else if (n >= 1 && n <= cd.sq.options.length) this.pickSide(n - 1);
      }
      else if (this.phase === 'rq') { if (n >= 1 && n <= 3) this.choose(n - 1); }
      else if (this.phase === 'verdict') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    cardText() {
      const cd = this.card;
      if (cd.kind === 'eoc') return this.sSolved ? { txt: cd.sq.options[cd.sq.answerIndex], question: false } : { txt: cd.sq.text, question: true };
      return { txt: cd.statement, question: false, speaker: cd.speaker };
    }
    lay() {
      const d = UI.docked(), ct = this.cardText();
      const key = this.i + '|' + (this.card && this.card.key) + '|' + ct.txt + '|' + d + '|' + Math.round(UI.text(30)) + '|' + (document.fonts ? document.fonts.status : '');
      let L = this.lays.get(key);
      if (!L) { L = layoutCard(ct.txt, { docked: d, question: ct.question, speaker: ct.speaker }); this.lays.set(key, L); }
      return L;
    }
    tray() { return trayGeo(UI.docked()); }
    sub() {
      if (!this.cards || this.phase === 'intro' || this.phase === 'end') return '';
      return `card ${this.i + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      g.fillStyle = paperTile(PAL.kraft, 3, 'tex_kraft'); g.fillRect(0, 0, Game.W, Game.H);
      UI.hud(TITLE, this.sub());
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(), Tr = this.tray();
      if (docked) this.drawJudge(1700, 1080, 620, this.verdictAt);
      else CV().prop('printing_press', 1600, 1062, 330, { seed: 805, ay: 1 });   // decoration under the panel (the panels cover it when they are tall)
      if (!docked && this.phase === 'verdict') this.drawJudgePeek(Tr);
      this.drawTray(Tr, docked);
      this.drawCard(L, Tr, docked);
      if (docked) return this.drawDock();
      if (this.phase === 'drop' || this.phase === 'sq') { if (this.card.kind === 'statement') this.drawSQ(); else this.drawOptions('sq'); }
      else if (this.phase === 'rq') this.drawOptions('rq');
      else this.drawVerdict();
    }
    drawJudge(x, y, h, t0) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: t0 != null && T - t0 < 2 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
    }
    /* desktop verdict: the Judge rises from behind the tray, over the place the card left */
    drawJudgePeek(Tr) {
      const k = E.outBack(P(T, this.verdictAt + 0.25, this.verdictAt + 0.75), 1.3);
      if (k <= 0) return;
      const x = this.card.side === 'federalist' ? Tr.x + 300 : Tr.x - 300;
      Puppet.draw('judge', x, Tr.top + 230 + (1 - k) * 520, 600, { facing: this.card.side === 'federalist' ? -1 : 1, t: T,
        env: T - this.verdictAt < 2 ? 0.25 + 0.25 * Math.sin(T * 17) : 0 });
    }

    /* the tray: a wooden frame, two coloured compartments with their woodtype letter + label, the sorted cards stacked */
    drawTray(Tr, docked) {
      const c = C();
      paperRect(Tr.x, Tr.y, round(Tr.w, 10), round(Tr.h, 10), WOOD, { seed: 801, depth: 1.4, boil: false });
      const fs = docked ? 40 : Math.round(Math.min(40, UI.text(30)));
      for (const side of Ratify.SIDES) {
        const S = SIDE(side), cp = Tr.comp(side);
        paperRect(cp.x, cp.y, cp.w, cp.h, S.color, { seed: side === 'federalist' ? 811 : 812, depth: 0.5, boil: false });
        // the letter block + the label along the compartment's top edge
        const ly = cp.y - cp.h / 2 + fs * 0.95;
        paperRect(cp.x - cp.w / 2 + fs * 1.1, ly, Math.round(fs * 1.5), Math.round(fs * 1.5), c.cream, { seed: side === 'federalist' ? 821 : 822, depth: 0.7, rot: -0.04, boil: false });
        g.save(); g.font = `${Math.round(fs * 1.1)}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = S.color;
        g.fillText(S.letter, cp.x - cp.w / 2 + fs * 1.1, ly + fs * 0.06);
        g.font = `${fs}px ${SLAB}`; g.textAlign = 'left'; g.fillStyle = S.ink;
        let lbl = S.label; const maxW = cp.w - fs * 2.4 - 20;
        let f = fs; while (f > 16 && measure(lbl, `${f}px ${SLAB}`) > maxW) f -= 1;
        g.font = `${f}px ${SLAB}`; g.fillText(lbl, cp.x - cp.w / 2 + fs * 2.1, ly + f * 0.06);
        g.restore();
        // the cards already sorted onto this side (earlier cards of this attempt): a pile of sheets
        const done = [];
        for (let j = 0; j < this.i; j++) if (this.cards[j] && this.cards[j].side === side) done.push(j);
        const pw = round(cp.w - 90, 10), ph = round((cardBox(docked).h) * Tr.sIn, 2);
        done.slice(-4).forEach((j, k) => {
          const px = cp.x + (k - 1.5) * 8, py = cp.y + fs * 0.7 + (k - 1.5) * -5;
          paperRect(px, py, pw, ph, '#f4ead2', { seed: 830 + (j % 6), depth: 0.9, rot: ((j * 37) % 7 - 3) * 0.006, boil: false });
          g.fillStyle = 'rgba(43,36,32,0.28)';
          for (let r = 0; r < 3; r++) g.fillRect(px - pw / 2 + 30, py - ph / 2 + ph * (0.34 + r * 0.2), pw * (r === 2 ? 0.45 : 0.8), 3);
        });
      }
    }

    /* the card: drops in, leans to its side once step 1 is settled (stamp), slides into the tray at the verdict */
    cardPose(L, Tr) {
      const cd = this.card, k = clamp((T - this.cardAt) / DROP);
      let x = L.x, y = L.cy - (1 - E.outCubic(k)) * 1150, rot = (1 - k) * -0.07 + Math.sin(k * Math.PI) * 0.015 - 0.006, s = 1;
      if (this.sAt != null) {
        const u = E.outCubic(P(T, this.sAt, this.sAt + 0.4)), dir = cd.side === 'federalist' ? -1 : 1;
        x += dir * 26 * u; rot += dir * 0.012 * u;
      }
      if (this.verdictAt != null) {
        const u = E.outCubic(P(T, this.verdictAt, this.verdictAt + SLIDE)), cp = Tr.comp(cd.side), fs = UI.docked() ? 40 : Math.round(Math.min(40, UI.text(30)));
        x = lerp(x, cp.x, u); y = lerp(y, cp.y + fs * 0.7, u); s = lerp(1, Tr.sIn, u); rot = lerp(rot, 0.01 * (cd.side === 'federalist' ? -1 : 1), u);
      }
      return { x, y, rot, s };
    }
    drawCard(L, Tr, docked) {
      const cd = this.card, Cv = CV(), c = C(), p = this.cardPose(L, Tr);
      g.save();
      g.translate(p.x, p.y); g.rotate(p.rot); g.scale(p.s, p.s); g.translate(-L.x, -L.cy);
      Cv.parchment(L.x, L.cy, round(L.w, 10), round(L.h, 10), { seed: 520 + (this.i % 4), depth: 1.6, color: '#f4ead2' });
      g.fillStyle = RULE_BLUE;
      for (let y = L.y0 + L.lh * 0.82; y < L.bottom - 30; y += L.lh) g.fillRect(L.left + 30, y, L.w - 60, 2);
      g.fillStyle = 'rgba(158,43,37,0.7)'; g.fillRect(L.left + 26, L.ruleY, L.w - 52, 3);
      // the header badge (neutral ink: the sides' colours are the answer)
      const lbl = cd.kind === 'eoc' ? 'ON THE EOC' : 'WHO WOULD SAY THIS?', bs = L.bs;
      const bw = round(measure(lbl, `${bs}px ${SLAB}`) + bs * 1.3, 10), bh = round(bs * 1.7, 2);
      const bx = L.left + 44 + bw / 2, by = L.top + (L.ruleY - L.top) / 2 + 2;
      paperRect(bx, by, bw, bh, INK, { seed: cd.kind === 'eoc' ? 541 : 542, depth: 0.8, rot: -0.02, boil: false });
      g.save(); g.translate(bx, by); g.rotate(-0.02); g.font = `${bs}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = c.cream; g.fillText(lbl, 0, bs * 0.05); g.restore();
      // the text (typewriter; the EOC question in slab type)
      g.save(); g.font = `${L.s}px ${L.font}`; g.fillStyle = INK; g.textAlign = 'left'; g.textBaseline = 'middle';
      L.lines.forEach((l, j) => g.fillText(l, L.tx, L.lineY[j]));
      // the footer: the card's number (left) and a quote's speaker (right: story, never the key)
      g.font = `${L.fs}px ${TYPE}`; g.fillStyle = 'rgba(43,36,32,0.6)'; g.fillText(`No. ${this.i + 1} of ${this.n}`, L.left + 44, L.bottom - L.fs * 1.1);
      if (L.spk) { g.font = `${L.fs}px ${HAND}`; g.fillStyle = PENCIL; g.textAlign = 'right'; g.fillText(L.spk, L.right - 44, L.bottom - L.fs * 1.1); }
      g.restore();
      // the side's stamp (the truth, once step 1 is settled)
      if (this.sAt != null) {
        const S = SIDE(cd.side);
        // between the header badge and the card's right edge (shrunk to fit when a phone's big badge type leaves less room)
        const raw = measure(S.short, "84px 'Special Elite'") + 80, room = L.right - 40 - (bx + bw / 2 + 24);
        const ss = Math.min(docked ? 0.62 : 0.5, room / raw);
        UI.stamp(S.short, L.right - 40 - raw * ss / 2, by + 6, { rot: -0.06, s: pop(T, this.sAt, 0.3) * ss, color: S.color });
      }
      g.restore();
    }

    /* ---- the right-hand panel (desktop / landscape) ---- */
    panelTop() { const hs = UI.hudSlot(0); return Math.max(150, hs.y + hs.r + 24); }
    drawPanel(x, y, w, h, seed) { CV().parchment(x + w / 2, y + h / 2, round(w, 10), round(h, 10), { seed, depth: 1.3 }); }
    /* statement step 1: the two side buttons */
    drawSQ() {
      const X = 1290, W = 600, top = this.panelTop(), c = C();
      const qs = Math.round(UI.text(44)), bs = Math.round(UI.text(30));
      const qL = UI._wrap(Ratify.SIDE_Q, W - 60, `${qs}px ${SLAB}`);
      const body = UI._wrap('Navy side or brick side of the tray? Think about what each side wanted.', W - 60, `${bs}px ${SERIF}`);
      const h = 30 + qL.length * qs * 1.15 + 14 + body.length * bs * 1.3 + 26;
      this.drawPanel(X, top, W, h, 610);
      text(qL, X + 30, top + 30, qs * 1.15, `${qs}px ${SLAB}`, INK);
      text(body, X + 30, top + 30 + qL.length * qs * 1.15 + 14, bs * 1.3, `${bs}px ${SERIF}`, INK);
      const bh = UI.tap(124), gap = 28, y1 = Math.min(top + h + 34 + bh / 2, 1080 - 30 - bh * 1.5 - gap);
      const live = this.t > 0.2, cd = this.card;
      const low = y1 + bh * 1.5 + gap;
      if (low < 700) this.drawJudgeSmall(X + W - 150, 1080, Math.min(420, 1080 - low - 20));
      cd.sq.options.forEach((o, i) => {
        const side = Ratify.SIDES[Ratify.SIDE_OPTIONS.indexOf(o)] || Ratify.SIDES[i], S = SIDE(side);
        const kh = UI.keys ? `${i + 1}  ` : '';
        if (live && UI.button('sd-s' + this.i + '_' + i, kh + o.toUpperCase(), X + W / 2, y1 + i * (bh + gap), { w: 560, h: 124, color: S.color, size: 46, font: SLAB })) this.pickSide(i);
      });
    }
    /* the option panel: EOC step 1 (4 statements) or step 2 (3 reasons); wrong picks crossed out with their why; the
       panel widens to the left over the card when a phone's big type doesn't fit */
    optCard(which) {
      const cd = this.card, q = which === 'sq' ? cd.sq : cd.rq, picks = which === 'sq' ? this.sPicks : this.picks;
      const key = which + '|' + this.i + '|' + picks.length + '|' + Math.round(UI.text(28));
      if (this._oc && this._oc.key === key) return this._oc;
      const top0 = this.panelTop();
      const qs = Math.round(UI.text(40)), ws = Math.round(UI.text(30)), ns = Math.round(UI.text(28));
      let os = Math.round(UI.text(which === 'sq' ? 30 : 32));
      const last = picks[picks.length - 1], why = last != null && last !== q.answerIndex ? ucFirst(q.why[last]) : '';
      const say = which === 'rq' && this.say ? this.say.text : '';
      let R;
      for (const X of [1290, 1150, 1000, 840, 680, 520]) {
        const W = 1890 - X;
        const note = say ? UI._wrap(say, W - 60, `${ns}px ${HAND}`) : [];
        // an EOC card already shows its question; the panel just says what to do (unless it covers the card)
        const qL = UI._wrap(which === 'sq' && X >= 1290 ? 'Pick the one statement that fits:' : q.text, W - 60, `${qs}px ${SLAB}`);
        const tw = W - 110;
        const opts = q.options.map(o => UI._wrap(o, tw, `${os}px ${SERIF}`));
        const rows = opts.map(ls => Math.max(UI.tap(92), ls.length * os * 1.25 + 26));
        const whyL = why ? UI._wrap(why, W - 60, `${ws}px ${HAND}`) : [];
        const nh = note.length ? note.length * ns * 1.15 + 14 : 0;
        const h = 26 + nh + qL.length * qs * 1.15 + 16 + rows.reduce((a, b) => a + b, 0) + 16 + whyL.length * ws * 1.15 + (whyL.length ? 16 : 0);
        const top = Math.max(96, Math.min(top0, 1070 - h));
        R = { key, which, X, W, note, ns, nh, qL, qs, opts, os, rows, whyL, ws, h, top, tw };
        if (top0 + h <= 1070) break;
      }
      return (this._oc = R);
    }
    drawOptions(which) {
      const R = this.optCard(which), { X, W, note, ns, nh, qL, qs, opts, os, rows, whyL, ws, h, top } = R, c = C();
      const cd = this.card, q = which === 'sq' ? cd.sq : cd.rq, picks = which === 'sq' ? this.sPicks : this.picks;
      if (which === 'rq' && T < this.qAt) return;
      if (which === 'sq' && this.t < 0.2) return;
      this.drawPanel(X, top, W, h, which === 'sq' ? 630 : 640);
      let y = top + 26;
      if (note.length) { note.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ns * 1.15, ns, this.say.at, { align: 'left', color: c.brick, seed: 650 + j, dur: 0.01 })); y += nh; }
      text(qL, X + 30, y, qs * 1.15, `${qs}px ${SLAB}`, INK); y += qL.length * qs * 1.15 + 16;
      const pickedAt = which === 'sq' ? this.sPickedAt : this.pickedAt;
      opts.forEach((ls, i) => {
        const rh = rows[i], cy = y + rh / 2, crossed = picks.includes(i);
        const hit = crossed ? { hover: false, clicked: false } : UI._hit('sd-' + which + this.i + '_' + i, X + W / 2, cy, W - 30, Math.max(rh - 6, UI.tap(0)));   // ≥ 44 CSS px on a phone (UI.tap)
        paperRect(X + W / 2, cy, round(W - 40, 10), round(rh - 10, 2), hit.hover ? '#fffaf0' : crossed ? '#e6dcc6' : PAL.cream, { seed: 660 + i, depth: hit.hover ? 1.4 : 0.6, boil: false });
        if (UI.keys) keyBadge(String(i + 1), X + 42, cy - Math.min(rh / 2 - 22, os * 0.5));
        const tx = X + 70, ty = cy - (ls.length - 1) * os * 1.25 / 2;
        text(ls, tx, ty - os * 0.625, os * 1.25, `${os}px ${SERIF}`, crossed ? PENCIL : INK);
        if (crossed) {
          const mw = Math.max(...ls.map(l => measure(l, `${os}px ${SERIF}`)));
          const a = i === picks[picks.length - 1] ? pickedAt : -1e9;
          ink([[tx - 6, cy + 10], [tx + 8 + Math.min(mw, R.tw), cy - 10]], { w: 5, color: c.brick, seed: 680 + i, prog: clamp((T - a) / 0.3), passes: 1 });
        }
        if (hit.clicked) { if (which === 'sq') this.pickSide(i); else this.choose(i); }
        y += rh;
      });
      y += 16;
      whyL.forEach((l, j) => hand(l, X + 30, y + (j + 0.5) * ws * 1.15, ws, pickedAt + 0.2 + j * 0.2, { align: 'left', color: c.brick, seed: 690 + j }));
    }
    /* the Judge's card: the side, his explain line, the stars, Next */
    verdictCard() {
      const key = this.i + '|' + Math.round(UI.text(28));
      if (this._vc && this._vc.key === key) return this._vc;
      const cd = this.card, r = this.results[this.i] || { stars: 0 }, top0 = this.panelTop(), bh = UI.tap(100);
      const S = { head: [Math.round(UI.text(46)), SLAB], who: [Math.round(UI.text(26)), TYPE], exp: [Math.round(UI.text(36)), HAND], stars: [Math.round(UI.text(34)), HAND] };
      const F = [['head', Ratify.SIDE_PLURAL[cd.side].toUpperCase() + '.'], ['who', 'JUDGE O:'], ['exp', cd.explain], ['stars', starLine(r)]];
      let R;
      for (const X of [1290, 1150, 1000, 840]) {
        const W = 1890 - X;
        const hs = Math.min(Math.round(UI.text(46)), Math.floor(Math.round(UI.text(46)) * (W - 70) / measure(F[0][1], `${Math.round(UI.text(46))}px ${SLAB}`)));
        S.head = [hs, SLAB];                                                     // the side's name on one line
        const rows = F.flatMap(([k, t]) => UI._wrap(t, W - 70, `${S[k][0]}px ${S[k][1]}`).map(l => [k, l]));
        const h = rows.reduce((a, [k]) => a + S[k][0] * 1.25, 0) + 50;
        const top = Math.max(96, Math.min(top0, 1080 - 14 - bh - 26 - h));
        R = { key, X, W, rows, h, S, bh, top };
        if (top0 + h + 26 + bh + 14 <= 1080) break;
      }
      return (this._vc = R);
    }
    drawVerdict() {
      const R = this.verdictCard(), { X, W, rows, h, S, bh, top } = R, c = C(), t0 = this.verdictAt + 0.2, cd = this.card;
      const by = top + h + 26 + bh / 2;
      this.drawPanel(X, top, W, h, 700);
      let y = top + 26, tt = t0;
      const col = { head: SIDE(cd.side).color, who: PENCIL, exp: INK, stars: c.navy };
      rows.forEach(([k, l]) => {
        const s = S[k][0], cy = y + s * 0.62;
        if (k === 'exp' || k === 'stars') hand(l, X + 34, cy, s, tt, { align: 'left', color: col[k], seed: 710 + Math.round(y) % 50, dur: 0.4 });
        else if (T >= tt) { g.save(); g.font = `${s}px ${S[k][1]}`; g.fillStyle = col[k]; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(l, X + 34, cy); g.restore(); }
        tt += 0.12; y += s * 1.25;
      });
      const label = this.i < this.n - 1 ? 'Next card ▶' : 'See my stars ▶';
      if (Game.time - this.verdictAt > NEXT_GUARD && UI.button('sd-next', label, X + W / 2, by, { w: 440, h: 100, color: c.brick, size: 48 })) this.next();
    }
    drawJudgeSmall(x, y, h) {
      const rise = E.outBack(P(T, this.enteredAt + 0.2, this.enteredAt + 0.7), 1.4);
      Puppet.draw('judge', x, y + (1 - rise) * 500, h, { facing: -1, t: T, env: 0 });
    }

    /* ---- portrait: the card and the tray are the picture; everything to read and tap is in the dock ---- */
    drawDock() {
      const cd = this.card, id = this.attempt + '_' + this.i, sq = cd.sq, rq = cd.rq;
      if (cd.kind === 'statement') {
        Dock.text('sd-scn' + id, [cd.statement + (cd.speaker ? ` (${cd.speaker})` : '')], { cls: 'note' });
        if (this.phase === 'drop' || this.phase === 'sq') {
          Dock.choices('sd-s' + id, { prompt: Ratify.SIDE_Q, options: sq.options, state: sq.options.map(() => ''), onChoose: i => this.pickSide(i) });
          return;
        }
        Dock.choices('sd-s' + id, { prompt: Ratify.SIDE_Q, options: sq.options, locked: true,
          state: sq.options.map((o, i) => (i === sq.answerIndex ? 'right' : this.sPicks.includes(i) ? 'wrong' : 'faded')) });
      } else {
        const st = sq.options.map((o, i) => (this.sPicks.includes(i) ? (i === sq.answerIndex ? 'right' : 'wrong') : this.sSolved ? 'faded' : ''));
        const notes = sq.options.map((o, i) => (this.sPicks.includes(i) && i !== sq.answerIndex ? ucFirst(sq.why[i]) : ''));
        Dock.choices('sd-s' + id, { prompt: sq.text, options: sq.options, state: st, notes, locked: this.sSolved, onChoose: i => this.pickSide(i) });
        if (!this.sSolved) return;
      }
      if (this.say) Dock.text('sd-say' + id, [this.say.text], { cls: 'wrong', reveal: true });
      if (this.phase === 'rq') {
        const st = rq.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : ''));
        const notes = rq.options.map((o, i) => (this.picks.includes(i) ? ucFirst(rq.why[i]) : ''));
        Dock.choices('sd-r' + id, { prompt: rq.text, options: rq.options, state: st, notes, onChoose: i => this.choose(i) });
        return;
      }
      const r = this.results[this.i] || { stars: 0 };
      Dock.choices('sd-r' + id, { prompt: rq.text, options: rq.options, locked: true, state: rq.options.map((o, i) => (i === rq.answerIndex ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')) });
      Dock.heading('sd-vh' + id, Ratify.SIDE_PLURAL[cd.side] + '.');
      Dock.text('sd-exp' + id, ['Judge O: ' + cd.explain], { cls: 'note' });
      Dock.text('sd-st' + id, [starLine(r)], { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.i < this.n - 1 ? 'Next card ▶' : 'See my stars ▶', label: 'next', aria: 'next card', go: true, onClick: () => this.next() });
    }

    introLines() {
      return [`${this.n} statements from the 1787 fight over the Constitution.`,
        'For each: which side would say it, Federalist or Anti-Federalist? Then: why would they say it?',
        'Federalists go on the navy side of the tray, Anti-Federalists on the brick side.', `${this.passStars} of ${this.of} stars to pass.`];
    }
    drawIntro(docked) {
      const t0 = this.enteredAt, c = C(), Cv = CV(), lines = this.introLines();
      this.drawJudge(docked ? 1600 : 330, 1080, docked ? 700 : 760);
      Cv.woodtype('WHICH SIDE?', docked ? 760 : 1150, docked ? 330 : 230, docked ? 110 : 90, { color: c.cream, ink: c.navy, t0: t0 + 0.2, seed: 740 });
      if (docked) {
        Cv.prop('printing_press', 760, 700, 440, { seed: 741 });
        for (const side of Ratify.SIDES) sideBadge(side, side === 'federalist' ? 430 : 1090, 700, 170, { s: pop(T, t0 + 0.6 + (side === 'anti' ? 0.15 : 0), 0.3), seed: side === 'anti' ? 743 : 742 });
        Dock.heading('sd-intro', TITLE, { cls: 'big' });
        Dock.text('sd-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start the cards', go: true, color: 'red', onClick: () => this.start() });
        return;
      }
      const fs = Math.round(UI.text(38));
      const nL = lines.reduce((a, l) => a + UI._wrap(l, 1000, `${fs}px ${SERIF}`).length, 0);
      const ph = nL * fs * 1.45 + 60;
      this.drawPanel(620, 320, 1060, ph, 745);
      let y = 380;
      lines.forEach((l, i) => UI._wrap(l, 1000, `${fs}px ${SERIF}`).forEach(tl => {
        if (T >= t0 + 0.5 + i * 0.3) { g.save(); g.font = `${i === 3 ? 'bold ' : ''}${fs}px ${SERIF}`; g.fillStyle = i === 3 ? c.brick : INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.globalAlpha = clamp((T - t0 - 0.5 - i * 0.3) / 0.3); g.fillText(tl, 1150, y); g.restore(); }
        y += fs * 1.45;
      }));
      const by = Math.min(990, y + 80);
      if (by < 900) for (const side of Ratify.SIDES) sideBadge(side, side === 'federalist' ? 800 : 1500, by, 130, { s: pop(T, t0 + 1.4 + (side === 'anti' ? 0.15 : 0), 0.3), seed: side === 'anti' ? 743 : 742 });
      if (this.t > 0.8 && UI.button('sd-start', 'Start ▶', 1150, by, { w: 420, h: 110, color: c.brick, size: 56 })) this.start();
    }
    drawEnd(docked) {
      const s = this.stars, of = this.of, btns = this.endButtons(), c = C(), Cv = CV();
      const rows = this.cards.map((cd, i) => ({ r: this.results[i] || { stars: 0 }, cd, txt: cd.kind === 'eoc' ? cd.sq.options[cd.sq.answerIndex] : cd.statement }));
      const msg = this.passed ? 'Passed! Back to the map to stamp it.' : `Not yet: ${this.passStars} stars to pass. New cards?`;
      if (docked) {
        Cv.woodtype(`${s} / ${of}`, 760, 380, 150, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 750 });
        UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 760, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6, color: c.brick });
        this.drawJudge(1640, 1080, 640);
        Dock.heading('sd-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('sd-end', [msg]);
        Dock.list('sd-end', rows.map((x, i) => ({ badge: String(i + 1), color: SIDE(x.cd.side).color,
          lines: [x.txt, `${Ratify.SIDE_LABEL[x.cd.side]} · ${x.r.stars} of 2 stars`] })));
        Dock.text('sd-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('sd-endb', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Cv.woodtype(`${s} / ${of}`, 700, 200, 100, { color: c.cream, ink: c.navy, t0: this.endAt + 0.1, seed: 750 });
      UI.stamp(this.passed ? 'PASSED' : 'TRY AGAIN', 1340, 200, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.0, color: c.brick });
      const fs = Math.round(UI.text(28)), rh = Math.max(UI.touch() ? 60 : 62, fs * 1.55), bh = UI.tap(100), ch = (rows.length + 1) * rh + 50;
      const cardTop = Math.max(280, Math.min(300, 1080 - 30 - bh - 26 - ch)), X0 = 180, W = 1560;
      this.drawPanel(X0, cardTop, W, ch, 760);
      const bsz = Math.round(rh * 0.78);
      rows.forEach((x, i) => {
        const y = cardTop + 25 + (i + 0.5) * rh;
        sideBadge(x.cd.side, X0 + 40 + bsz / 2, y, bsz, { seed: 770 + i });
        let t = x.txt;
        g.save(); g.font = `${fs}px ${SERIF}`;
        const maxW = W - 100 - bsz - 200;
        if (g.measureText(t).width > maxW) { while (g.measureText(t + '…').width > maxW && t.length > 8) t = t.slice(0, -1); t = t.trimEnd() + '…'; }
        g.fillStyle = SIDE(x.cd.side).color; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(t, X0 + 60 + bsz, y); g.restore();
        hand(`${x.r.stars} of 2`, X0 + W - 50, y, fs * 1.2, this.endAt + 0.4 + i * 0.08, { align: 'right', color: x.r.stars === 2 ? c.navy : c.brick, seed: 790 + i });
      });
      const py = cardTop + 25 + (rows.length + 0.5) * rh, by = Math.min(1080 - 24 - bh / 2, cardTop + ch + 26 + bh / 2);
      hand(msg, X0 + W / 2, py, Math.round(UI.text(38)), this.endAt + 0.8, { color: c.brick, seed: 799, dur: 0.5 });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('sd-end' + i, b.label, 700 + i * 520, by, { w: 480, h: 100, color: i === 0 ? c.brick : c.navy, size: 42 })) b.go(); });
      hand(`quiz code ${this.seed}`, 1880, by - bh / 2 - 22, 28, this.endAt + 0.6, { align: 'right', color: PAL.ink, seed: 798 });
    }
  }

  /* small helpers */
  const an = w => (/^[AEIOU]/.test(w) ? 'An' : 'A');
  function starLine(r) { return `+${r.stars} star${r.stars === 1 ? '' : 's'} (side ${r.sStar || 0}, reason ${r.rStar || 0})`; }
  function text(lines, x, yTop, lh, font, color) {
    g.save(); g.font = font; g.fillStyle = color; g.textAlign = 'left'; g.textBaseline = 'middle';
    lines.forEach((l, j) => g.fillText(l, x, yTop + (j + 0.5) * lh));
    g.restore();
  }
  function keyBadge(txt, x, y) {
    g.save(); g.fillStyle = INK; g.beginPath(); g.arc(x, y, 16, 0, TAU); g.fill();
    g.font = `20px ${SLAB}`; g.fillStyle = C().cream; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, x, y + 1); g.restore();
  }

  Sides.STEP = STEP;
  Sides.layout = layoutCard;
  Sides.tray = trayGeo;
  Sides.sideBadge = sideBadge;
  Object.defineProperty(Sides, 'solution', { get() { const s = Game.current(); return s instanceof Sides ? s.solution : null; } });
  /* tests / screenshots: swap the current card for the pool item `id` (drawn by the model with a fixed seed) */
  Sides.preview = (id, seed = 1) => {
    const s = Game.current(); if (!(s instanceof Sides) || !s.cards) return false;
    const it = json().items.find(x => x.id === id); if (!it) return false;
    s.cards[s.i] = Ratify.sideCard(it, Bank.rng(seed), {});
    s.lays.clear(); s._oc = s._vc = null;
    return true;
  };
  Levels.sides = Sides;
})();
